import { useCallback, useEffect, useState } from "react";
import { Loader2, LogOut, Trash2, Download } from "lucide-react";
import { api, apiError } from "../lib/api";
import { LogoFull } from "../components/site/Logo";
import { ArtworksAdmin } from "../components/admin/ArtworksAdmin";
import { SiteSettings } from "../components/admin/SiteSettings";
import { ContentEditor } from "../components/admin/ContentEditor";
import { PasswordForm } from "../components/admin/PasswordForm";
import { ForgotPassword, ResetPassword } from "../components/admin/ResetPassword";

const fmt = (d) => new Date(d).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" });

const Login = ({ onDone, onForgot }) => {
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/login", f); localStorage.setItem("cp_token", data.token); onDone(data.user); }
    catch (ex) { setErr(apiError(ex, "Connexion impossible.")); } finally { setBusy(false); }
  };
  const inp = "w-full border-b border-[#15202B]/30 bg-transparent py-3 outline-none focus:border-[#15202B]";
  return (
    <form onSubmit={submit} className="w-full max-w-sm bg-white p-8 sm:p-10 shadow-xl grid gap-5" data-testid="admin-login-modal">
      <LogoFull className="h-16 text-[#15202B] justify-self-start" />
      <h1 className="f-display text-4xl font-light">Espace artiste</h1>
      <input className={inp} type="email" placeholder="Courriel" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="admin-email-input" aria-label="Courriel" />
      <input className={inp} type="password" placeholder="Mot de passe" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="admin-password-input" aria-label="Mot de passe" />
      {err && <p className="text-sm text-[#A2372F]" data-testid="admin-login-error">{err}</p>}
      <button className="btn-ink justify-center" disabled={busy} data-testid="admin-login-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Se connecter"}</button>
      <button type="button" onClick={onForgot} className="text-sm text-[#3E6173] underline underline-offset-4 justify-self-start" data-testid="admin-forgot-password-link">Mot de passe oublié ?</button>
      <a href="/" className="text-sm text-[#3E6173] underline underline-offset-4" data-testid="admin-back-link">← Retour au site</a>
    </form>
  );
};

const exportCsv = (subs) => {
  const rows = [["Prénom", "Courriel", "Date"], ...subs.map((s) => [s.first_name, s.email, s.created_at])];
  const blob = new Blob(["\ufeff" + rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n")], { type: "text/csv" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "infolettre.csv"; a.click();
};

const Dashboard = ({ onLogout }) => {
  const [tab, setTab] = useState("artworks");
  const [msgs, setMsgs] = useState([]); const [subs, setSubs] = useState([]);
  const [mode, setMode] = useState(null);
  useEffect(() => { api.get("/admin/payment-mode").then((r) => setMode(r.data?.mode)).catch(() => {}); }, []);
  const load = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([api.get("/admin/messages"), api.get("/admin/subscribers")]);
      setMsgs(Array.isArray(m.data) ? m.data : []); setSubs(Array.isArray(s.data) ? s.data : []);
    } catch (e) { if (e?.response?.status === 401) onLogout(); }
  }, [onLogout]);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { await api.delete(`/admin/messages/${id}`); setMsgs((p) => p.filter((m) => m.id !== id)); };

  return (
    <div className="w-full max-w-5xl" data-testid="admin-dashboard-container">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="f-display text-4xl sm:text-5xl font-light">Bonjour Christine</h1>
        {mode && (
          <span data-testid="admin-payment-mode" className={`text-xs tracking-[0.12em] uppercase px-3 py-1.5 rounded-full ${mode === "production" ? "bg-[#DDEBDD] text-[#24502C]" : mode === "sandbox" ? "bg-[#F6E3B8] text-[#6B4A0E]" : "bg-[#F7D6D2] text-[#8A2A22]"}`}>
            {mode === "production" ? "Paiements réels actifs" : mode === "sandbox" ? "Paiements en mode test" : "Paiement Square indisponible"}
          </span>
        )}
        <div className="flex flex-wrap gap-2">
          <a href="/" className="btn-line" data-testid="admin-view-site-link">Voir le site</a>
          <button onClick={onLogout} className="btn-line" data-testid="admin-logout-button"><LogOut className="w-4 h-4" />Déconnexion</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {[["artworks", "Œuvres"], ["site", "Page d'accueil"], ["content", "Textes et photos"], ["messages", `Messages (${msgs.length})`], ["subs", `Infolettre (${subs.length})`], ["account", "Mon compte"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k === "subs" ? "subscribers" : k}`}
            className={`px-5 py-2.5 rounded-full text-sm border ${tab === k ? "bg-[#15202B] text-white border-[#15202B]" : "border-[#15202B]/20"}`}>{l}</button>
        ))}
      </div>
      {tab === "artworks" ? <ArtworksAdmin /> : tab === "site" ? <SiteSettings /> : tab === "content" ? <ContentEditor /> : tab === "account" ? <PasswordForm /> : tab === "messages" ? (
        <div className="grid gap-4">
          {msgs.length === 0 && <p className="text-[#55636F]">Aucun message pour le moment.</p>}
          {msgs.map((m) => (
            <article key={m.id} className="bg-white p-6 shadow-sm" data-testid="admin-inquiry-row">
              <div className="flex flex-wrap justify-between gap-3">
                <div><p className="font-medium">{m.name}</p><a href={`mailto:${m.email}`} className="text-sm text-[#3E6173] underline">{m.email}</a>{m.phone && <span className="text-sm text-[#55636F]"> · {m.phone}</span>}</div>
                <div className="flex items-start gap-3 text-sm text-[#55636F]">{fmt(m.created_at)}
                  <button onClick={() => del(m.id)} aria-label="Supprimer" data-testid="admin-delete-message-button" className="text-[#A2372F]"><Trash2 className="w-4 h-4" /></button></div>
              </div>
              {m.artwork && <p className="mt-3 text-xs tracking-[0.2em] uppercase text-[#8A6420]">{m.artwork}</p>}
              <p className="mt-2 whitespace-pre-line text-[#2E3B47]">{m.message}</p>
            </article>
          ))}
        </div>
      ) : (
        <div className="bg-white p-6 shadow-sm">
          <button onClick={() => exportCsv(subs)} className="btn-line mb-4" data-testid="admin-export-csv-button"><Download className="w-4 h-4" />Exporter en CSV</button>
          {subs.length === 0 && <p className="text-[#55636F]">Aucun abonné pour le moment.</p>}
          {subs.map((s) => (
            <div key={s.id} className="flex flex-wrap justify-between gap-2 py-3 border-b border-[#15202B]/10 text-sm" data-testid="admin-subscriber-row">
              <span>{s.first_name} · {s.email}</span><span className="text-[#55636F]">{fmt(s.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    if (!localStorage.getItem("cp_token")) { setUser(false); return; }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => { localStorage.removeItem("cp_token"); setUser(false); });
  }, []);
  const logout = useCallback(() => { localStorage.removeItem("cp_token"); setUser(false); }, []);
  const [view, setView] = useState("login");
  const [resetToken, setResetToken] = useState(() => new URLSearchParams(window.location.search).get("reinitialiser"));
  const toLogin = () => { setResetToken(null); setView("login"); window.history.replaceState(null, "", "/admin"); };
  const guest = resetToken ? <ResetPassword token={resetToken} onDone={toLogin} />
    : view === "forgot" ? <ForgotPassword onBack={() => setView("login")} />
    : <Login onDone={setUser} onForgot={() => setView("forgot")} />;
  return (
    <div className="min-h-screen bg-[#F6F1E9] text-[#15202B] f-body flex items-start sm:items-center justify-center px-5 py-16">
      {user === null ? <Loader2 className="w-6 h-6 animate-spin" /> : user && !resetToken ? <Dashboard onLogout={logout} /> : guest}
    </div>
  );
}
